import React, { useState, useEffect } from 'react';
import { 
  Upload, 
  FileText, 
  Download, 
  Trash2, 
  Eye, 
  EyeOff,
  Shield,
  AlertCircle,
  Plus,
  Loader2
} from 'lucide-react';

import { WebIDData } from '../../services/webids';
import { useAuth } from '../../context/AuthContext';
import { 
  getMedicalDocuments, 
  uploadMedicalDocument, 
  deleteMedicalDocument,
  MedicalDoc,
  MedicalDocType 
} from '../../services/medicalDocuments';
import { Button } from '../ui';

/**
 * @component MedicalDocuments
 * @description Gestión de documentos médicos cifrados
 */

interface MedicalDocumentsProps {
  webId: WebIDData;
  onUpdate: () => void;
}

const MedicalDocuments: React.FC<MedicalDocumentsProps> = ({ webId, onUpdate }) => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState<MedicalDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showUploadForm, setShowUploadForm] = useState(false);

  useEffect(() => {
    loadDocuments();
  }, [webId.id]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      const docs = await getMedicalDocuments(webId.id);
      setDocuments(docs);
    } catch (err) {
      console.error('Error loading documents:', err);
      setError('Error al cargar documentos médicos');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file: File, docData: {
    name: string;
    type: MedicalDocType;
    description?: string;
    isPublic: boolean;
    allowEmergencyAccess: boolean;
  }) => {
    if (!user) return;

    try {
      setUploading(true);
      await uploadMedicalDocument(webId.id, user.uid, file, docData);
      await loadDocuments();
      setShowUploadForm(false);
    } catch (err) {
      console.error('Error uploading document:', err);
      alert('Error al subir documento');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId: string) => {
    if (!user) return;

    const confirmed = window.confirm('¿Estás seguro de que quieres eliminar este documento?');
    if (!confirmed) return;

    try {
      await deleteMedicalDocument(docId, user.uid);
      await loadDocuments();
    } catch (err) {
      console.error('Error deleting document:', err);
      alert('Error al eliminar documento');
    }
  };

  const getDocTypeIcon = (type: MedicalDocType) => {
    switch (type) {
      case 'prescription':
        return '💊';
      case 'allergy_card':
        return '⚠️';
      case 'medical_report':
        return '📋';
      case 'insurance_card':
        return '🏥';
      default:
        return '📄';
    }
  };

  const getDocTypeName = (type: MedicalDocType) => {
    switch (type) {
      case 'prescription':
        return 'Receta Médica';
      case 'allergy_card':
        return 'Tarjeta de Alergias';
      case 'medical_report':
        return 'Informe Médico';
      case 'insurance_card':
        return 'Tarjeta de Seguro';
      default:
        return 'Otro';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Documentos Médicos
          </h3>
          <p className="text-sm text-gray-600">
            Almacena documentos médicos importantes de forma segura y cifrada
          </p>
        </div>
        
        <Button
          onClick={() => setShowUploadForm(true)}
          disabled={uploading}
        >
          <Plus className="h-4 w-4 mr-2" />
          Subir Documento
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Security Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 mb-1">
              Seguridad y Privacidad
            </h4>
            <p className="text-sm text-blue-700">
              Todos los documentos se almacenan cifrados. Solo tú y las personas autorizadas 
              pueden acceder a esta información. Los documentos marcados como "Acceso de Emergencia" 
              estarán disponibles para personal médico en situaciones críticas.
            </p>
          </div>
        </div>
      </div>

      {/* Documents List */}
      <div className="space-y-4">
        {documents.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No hay documentos médicos
            </h3>
            <p className="text-gray-600 mb-6">
              Sube documentos importantes como recetas, informes médicos o tarjetas de seguro.
            </p>
            <Button
              onClick={() => setShowUploadForm(true)}
              disabled={uploading}
            >
              <Plus className="h-4 w-4 mr-2" />
              Subir primer documento
            </Button>
          </div>
        ) : (
          documents.map((doc) => (
            <div
              key={doc.id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <div className="text-2xl">
                    {getDocTypeIcon(doc.type)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 truncate">
                      {doc.name}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {getDocTypeName(doc.type)}
                    </p>
                    {doc.description && (
                      <p className="text-sm text-gray-500 mt-1">
                        {doc.description}
                      </p>
                    )}
                    
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span>{formatFileSize(doc.fileSize)}</span>
                      <span>
                        Subido: {doc.uploadedAt.toDate().toLocaleDateString('es-CL')}
                      </span>
                      {doc.expiresAt && (
                        <span className="text-orange-600">
                          Expira: {doc.expiresAt.toDate().toLocaleDateString('es-CL')}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-3 mt-2">
                      <div className="flex items-center gap-1">
                        {doc.isPublic ? (
                          <Eye className="h-3 w-3 text-green-600" />
                        ) : (
                          <EyeOff className="h-3 w-3 text-gray-400" />
                        )}
                        <span className="text-xs text-gray-600">
                          {doc.isPublic ? 'Público' : 'Privado'}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        <Shield className="h-3 w-3 text-blue-600" />
                        <span className="text-xs text-gray-600">
                          {doc.allowEmergencyAccess ? 'Acceso emergencia' : 'Solo propietario'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(doc.fileUrl, '_blank')}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(doc.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Upload Form Modal */}
      {showUploadForm && (
        <UploadDocumentModal
          onUpload={handleUpload}
          onClose={() => setShowUploadForm(false)}
          uploading={uploading}
        />
      )}
    </div>
  );
};

// Modal para subir documentos
interface UploadDocumentModalProps {
  onUpload: (file: File, docData: {
    name: string;
    type: MedicalDocType;
    description?: string;
    isPublic: boolean;
    allowEmergencyAccess: boolean;
  }) => Promise<void>;
  onClose: () => void;
  uploading: boolean;
}

const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({
  onUpload,
  onClose,
  uploading,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [type, setType] = useState<MedicalDocType>('other');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [allowEmergencyAccess, setAllowEmergencyAccess] = useState(true);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      if (!name) {
        setName(selectedFile.name.replace(/\.[^/.]+$/, ''));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !name.trim()) return;

    await onUpload(file, {
      name: name.trim(),
      type,
      description: description.trim() || undefined,
      isPublic,
      allowEmergencyAccess,
    });
  };

  const maxFileSize = 10 * 1024 * 1024; // 10MB
  const allowedTypes = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'text/plain',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ];

  const isValidFile = file && file.size <= maxFileSize && allowedTypes.includes(file.type);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="w-full max-w-lg bg-white rounded-lg shadow-xl">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Subir Documento Médico
          </h3>
          <button
            onClick={onClose}
            disabled={uploading}
            className="text-gray-400 hover:text-gray-600"
          >
            <span className="text-xl">×</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Archivo *
            </label>
            <input
              type="file"
              onChange={handleFileChange}
              accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.txt,.doc,.docx"
              disabled={uploading}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            {file && !isValidFile && (
              <p className="mt-1 text-sm text-red-600">
                Archivo no válido. Máximo 10MB. Tipos permitidos: PDF, imágenes, documentos de texto.
              </p>
            )}
          </div>

          {/* Document Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nombre del Documento *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={uploading}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Ej: Receta Cardiología Enero 2024"
            />
          </div>

          {/* Document Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Documento
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as MedicalDocType)}
              disabled={uploading}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="prescription">Receta Médica</option>
              <option value="allergy_card">Tarjeta de Alergias</option>
              <option value="medical_report">Informe Médico</option>
              <option value="insurance_card">Tarjeta de Seguro</option>
              <option value="other">Otro</option>
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descripción (Opcional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={uploading}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Información adicional sobre el documento..."
            />
          </div>

          {/* Privacy Settings */}
          <div className="space-y-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isPublic"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                disabled={uploading}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="isPublic" className="ml-2 block text-sm text-gray-700">
                Hacer público (visible en perfil de emergencia)
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="allowEmergencyAccess"
                checked={allowEmergencyAccess}
                onChange={(e) => setAllowEmergencyAccess(e.target.checked)}
                disabled={uploading}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="allowEmergencyAccess" className="ml-2 block text-sm text-gray-700">
                Permitir acceso en emergencias (recomendado)
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={uploading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!file || !name.trim() || !isValidFile || uploading}
            >
              {uploading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Subiendo...
                </div>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Subir Documento
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MedicalDocuments;
